import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectProjects } from "../features/projects/projectSlice";
import { fetchProjectsAsync } from "../features/projects/projectApi";
import { ManagedMilestone } from "../features/milestoneManagement/ManagedMilestone";
import {
  deleteMilestone,
  fetchManagedMilestones,
  renameMilestone,
} from "../features/milestoneManagement/milestoneManagementApi";
import {
  selectManagedMilestones,
  selectManagedMilestonesLoading,
  selectMilestoneManagementMutating,
} from "../features/milestoneManagement/milestoneManagementSlice";

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

interface Notification {
  open: boolean;
  message: string;
  severity: "success" | "warning" | "error";
}

const MilestoneManagementPage = () => {
  const dispatch = useAppDispatch();
  const milestones = useAppSelector(selectManagedMilestones);
  const loading = useAppSelector(selectManagedMilestonesLoading);
  const mutating = useAppSelector(selectMilestoneManagementMutating);
  const projects = useAppSelector(selectProjects);

  const [projectFilter, setProjectFilter] = useState<number | "all">("all");
  const [renameTarget, setRenameTarget] = useState<ManagedMilestone | null>(
    null
  );
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ManagedMilestone | null>(
    null
  );
  const [notification, setNotification] = useState<Notification>({
    open: false,
    message: "",
    severity: "success",
  });

  // Ensure the project filter is populated even when navigating here directly
  // (projects are otherwise only fetched on the home / hours pages).
  useEffect(() => {
    dispatch(fetchProjectsAsync());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchManagedMilestones(
        projectFilter === "all" ? undefined : projectFilter
      )
    );
  }, [dispatch, projectFilter]);

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => a.name.localeCompare(b.name)),
    [projects]
  );

  const notify = (
    message: string,
    severity: Notification["severity"] = "success"
  ) => setNotification({ open: true, message, severity });

  const openRename = (m: ManagedMilestone) => {
    setRenameTarget(m);
    setRenameValue(m.name);
  };

  const confirmRename = async () => {
    if (!renameTarget) return;
    const name = renameValue.trim();
    if (!name || name === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    const result = await dispatch(
      renameMilestone({ milestoneId: renameTarget.id, name })
    );
    if (renameMilestone.fulfilled.match(result)) {
      notify("Milestone renamed.");
      setRenameTarget(null);
    } else {
      notify((result.payload as string) ?? "Failed to rename milestone.", "error");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await dispatch(deleteMilestone(deleteTarget.id));
    if (deleteMilestone.fulfilled.match(result)) {
      notify("Milestone and its data deleted.");
      setDeleteTarget(null);
    } else {
      notify((result.payload as string) ?? "Failed to delete milestone.", "error");
    }
  };

  return (
    <Container maxWidth="xl" sx={{ pt: 4, pb: 6, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontWeight: 700, letterSpacing: "-0.5px", mb: 1 }}
        >
          Manage Milestones
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Admin-only management of milestones. Rename a milestone, or delete it
          together with all of its tasks, time entries, and time-series data
          points. Deletion cannot be undone.
        </Typography>
      </Box>

      <Box sx={{ mb: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Project</InputLabel>
          <Select
            label="Project"
            value={projectFilter}
            onChange={(e) =>
              setProjectFilter(
                e.target.value === "all" ? "all" : Number(e.target.value)
              )
            }
          >
            <MenuItem value="all">All projects</MenuItem>
            {sortedProjects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip label={`${milestones.length} milestones`} />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Milestone</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Tasks
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Data points
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {milestones.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ p: 2, textAlign: "center" }}
                    >
                      No milestones found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                milestones.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>{m.name}</TableCell>
                    <TableCell>{m.projectName}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={m.state || "—"}
                        color={m.state === "closed" ? "default" : "info"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(m.gitLabCreateDate)}</TableCell>
                    <TableCell align="right">{m.taskCount}</TableCell>
                    <TableCell align="center">
                      {m.hasDataPoints ? (
                        <Tooltip title={`${m.dataPointCount} data points`}>
                          <Chip
                            size="small"
                            label={`Yes (${m.dataPointCount})`}
                            color="success"
                          />
                        </Tooltip>
                      ) : (
                        <Chip
                          size="small"
                          label="No"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Rename">
                        <IconButton
                          size="small"
                          onClick={() => openRename(m)}
                          disabled={mutating}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete milestone and all its data">
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(m)}
                          disabled={mutating}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Rename dialog */}
      <Dialog
        open={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Rename milestone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Milestone name"
            fullWidth
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmRename();
            }}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)} disabled={mutating}>
            Cancel
          </Button>
          <Button
            onClick={confirmRename}
            variant="contained"
            disabled={mutating || !renameValue.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Delete milestone?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete{" "}
            <strong>{deleteTarget?.name}</strong> from{" "}
            <strong>{deleteTarget?.projectName}</strong> and all of its
            associated data: {deleteTarget?.taskCount ?? 0} task(s),
            {" "}
            {deleteTarget?.dataPointCount ?? 0} data point(s), plus related
            time entries, notes, and forecasts. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={mutating}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={mutating}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification((n) => ({ ...n, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Fade}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification((n) => ({ ...n, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MilestoneManagementPage;
